import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { capture } from "@/lib/analytics/posthog";
import {
    GraduationCap,
    Heart,
    Share2,
    Sparkles,
    BadgeCheck,
    Clock,
} from "lucide-react";

const STUDENT_STEPS = [
    {
        icon: Sparkles,
        title: "Build your page in 60 seconds",
        body: "Photo, your story, a goal. We'll help you write it.",
    },
    {
        icon: Share2,
        title: "Share with your inner circle first",
        body: "Family group chats raise more than public posts. We give you the share kit.",
    },
    {
        icon: BadgeCheck,
        title: "Funds land in your bank",
        body: "Stripe-verified payouts. Receipts emailed. You stay in control.",
    },
];

const SUPPORTER_STEPS = [
    {
        icon: Heart,
        title: "Pick a student you care about",
        body: "Open their page. See what they're building.",
    },
    {
        icon: Clock,
        title: "Give once — or $25/month",
        body: "Apple Pay or card. Cancel any time, no questions asked.",
    },
    {
        icon: GraduationCap,
        title: "Follow their progress",
        body: "Get short updates straight from the student. Be part of the story.",
    },
];

const HowItWorksSection = () => {
    const { ref, isVisible } = useScrollAnimation(0.05);

    return (
        <section
            ref={ref}
            id="how-it-works"
            className="py-20 md:py-28 relative overflow-hidden"
            style={{ background: "linear-gradient(180deg, #F4F8FC 0%, #FFFFFF 100%)" }}
        >
            <div className="max-w-6xl mx-auto px-6">
                <div
                    className={`text-center max-w-2xl mx-auto mb-14 transition-all duration-700 ${
                        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                    }`}
                >
                    <h2 className="text-3xl md:text-5xl font-extrabold text-primary leading-tight tracking-tight">
                        Built for both sides of the relationship
                    </h2>
                    <p className="text-lg text-slate-600 mt-4">
                        Whether you're the student or the one cheering them on — three steps, no awkward asks.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                    {/* Student track */}
                    <div
                        className={`relative bg-white rounded-3xl p-7 md:p-9 border border-slate-200/70 shadow-sm hover:shadow-xl transition-all duration-500 ${
                            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                        }`}
                    >
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-11 h-11 rounded-xl bg-primary text-white flex items-center justify-center">
                                <GraduationCap className="w-6 h-6" />
                            </div>
                            <div>
                                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                                    For students
                                </p>
                                <h3 className="text-2xl font-bold text-primary leading-tight">
                                    Get your Fund Page live today
                                </h3>
                            </div>
                        </div>

                        <ol className="space-y-5 mb-7">
                            {STUDENT_STEPS.map(({ icon: Icon, title, body }, i) => (
                                <li key={title} className="flex items-start gap-4">
                                    <div className="relative flex-shrink-0">
                                        <div className="w-10 h-10 rounded-full bg-primary/5 border border-primary/10 flex items-center justify-center">
                                            <Icon className="w-5 h-5 text-primary" />
                                        </div>
                                        <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center">
                                            {i + 1}
                                        </span>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-primary leading-tight">
                                            {title}
                                        </h4>
                                        <p className="text-slate-600 text-[15px] mt-1 leading-relaxed">
                                            {body}
                                        </p>
                                    </div>
                                </li>
                            ))}
                        </ol>

                        <Link
                            to="/signup-creator"
                            onClick={() => capture('cta_clicked', { cta: 'start_fund_page', location: 'how_it_works_section' })}
                        >
                            <Button
                                size="lg"
                                className="w-full bg-primary hover:bg-primary/90 text-white text-base md:text-lg px-7 py-6 rounded-full font-semibold"
                            >
                                Start your Fund Page — free
                            </Button>
                        </Link>
                        <p className="text-xs text-slate-500 text-center mt-3">
                            No credit card. 5-7% platform fee for supporters, added on top — students keep 100%.
                        </p>
                    </div>

                    {/* Supporter track */}
                    <div
                        className={`relative rounded-3xl p-7 md:p-9 shadow-sm hover:shadow-xl transition-all duration-500 delay-100 ${
                            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                        }`}
                        style={{
                            background:
                                "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary) / 0.85) 100%)",
                        }}
                    >
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur text-white flex items-center justify-center ring-1 ring-white/20">
                                <Heart className="w-6 h-6" />
                            </div>
                            <div>
                                <p className="text-xs font-bold uppercase tracking-wider text-white/60">
                                    For family, friends &amp; community
                                </p>
                                <h3 className="text-2xl font-bold text-white leading-tight">
                                    Back a student you believe in
                                </h3>
                            </div>
                        </div>

                        <ol className="space-y-5 mb-7">
                            {SUPPORTER_STEPS.map(({ icon: Icon, title, body }, i) => (
                                <li key={title} className="flex items-start gap-4">
                                    <div className="relative flex-shrink-0">
                                        <div className="w-10 h-10 rounded-full bg-white/10 ring-1 ring-white/20 flex items-center justify-center">
                                            <Icon className="w-5 h-5 text-white" />
                                        </div>
                                        <span
                                            className="absolute -top-1 -right-1 w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center text-primary"
                                            style={{ background: "hsl(var(--gold))" }}
                                        >
                                            {i + 1}
                                        </span>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-white leading-tight">
                                            {title}
                                        </h4>
                                        <p className="text-white/75 text-[15px] mt-1 leading-relaxed">
                                            {body}
                                        </p>
                                    </div>
                                </li>
                            ))}
                        </ol>

                        <Link to="/fund/demo">
                            <Button
                                size="lg"
                                className="w-full bg-white hover:bg-white/90 text-primary text-base md:text-lg px-7 py-6 rounded-full font-semibold"
                            >
                                See a real Fund Page
                            </Button>
                        </Link>
                        <p className="text-xs text-white/60 text-center mt-3">
                            Apple Pay &amp; card. Receipts emailed instantly.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default HowItWorksSection;
